import React, { useEffect, useRef } from "react";

/**
 * Trustpilot TrustBox widget.
 * Renders once the Trustpilot bootstrap script loaded from public/index.html
 * discovers this container. The Business Unit ID is read from
 * REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID at build time — falls back to a
 * compact "review us" callout if not configured yet.
 *
 * Templates reference: https://support.trustpilot.com/hc/en-us/articles/115011421468
 *   micro-review-count : "5abcd..."
 *   micro-star         : "5419b6a8b0d04a076446a9ad"
 *   micro-combo        : "5419b6ffb0d04a076446a9af"
 *   mini               : "53aa8807dec7e10d38f59f32"
 *   review-collector   : "56278e9abfbbba0bdcd568bc"
 */
const DEFAULT_REVIEW_URL = "https://www.trustpilot.com/review/helixorapeptides.com";
const REVIEW_WRITE_URL = "https://www.trustpilot.com/evaluate/helixorapeptides.com";
const DOMAIN = "helixorapeptides.com";

const TEMPLATE_MAP = {
    "micro-combo": "5419b6ffb0d04a076446a9af",
    "micro-star": "5419b6a8b0d04a076446a9ad",
    "mini": "53aa8807dec7e10d38f59f32",
    "review-collector": "56278e9abfbbba0bdcd568bc",
};

export const TrustpilotWidget = ({
    variant = "micro-combo",
    theme = "dark",
    className = "",
    height = "24px",
    width = "100%",
    align = "left",
}) => {
    const ref = useRef(null);
    const buid = process.env.REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID;

    useEffect(() => {
        // Ask the Trustpilot script to render this instance once it is available.
        if (buid && ref.current && window.Trustpilot) {
            window.Trustpilot.loadFromElement(ref.current, true);
        }
    }, [buid]);

    // Fallback: no BUID configured — show a compact "review us" callout so the
    // block never renders empty. Once REACT_APP_TRUSTPILOT_BUSINESS_UNIT_ID is
    // set at build time, the real TrustBox renders in its place automatically.
    if (!buid) {
        return (
            <a
                href={REVIEW_WRITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.15em] text-ink-dim hover:text-neon transition-colors ${className}`}
                data-testid="trustpilot-fallback-link"
            >
                <span className="inline-block w-2 h-2 rounded-full bg-[#00B67A]" />
                REVIEW US ON TRUSTPILOT →
            </a>
        );
    }

    return (
        <div
            ref={ref}
            className={`trustpilot-widget ${className}`}
            data-locale="en-US"
            data-template-id={TEMPLATE_MAP[variant] || TEMPLATE_MAP["micro-combo"]}
            data-businessunit-id={buid}
            data-style-height={height}
            data-style-width={width}
            data-theme={theme}
            data-text-color={theme === "dark" ? "#ffffff" : "#0a0f1a"}
            data-style-alignment={align}
            data-testid={`trustpilot-widget-${variant}`}
        >
            <a href={DEFAULT_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                Trustpilot reviews · {DOMAIN}
            </a>
        </div>
    );
};

export default TrustpilotWidget;
